﻿import { useState, useEffect, useRef } from 'react';
import { Select, Typography, Empty, Divider, Space, Tag } from 'antd';
import useDashboardStore from '../../stores/dashboardStore';
import type { DataSourceConfig } from '@dtwin/shared';
import DataSourceConfigForm, { DataParserEditor } from './DataSourceConfigForm';
import { fetchDataSources } from '../../api/dataSources';
import { fetchDataSource } from '../../services/dataProvider';

const { Text, Title } = Typography;

const TYPE_LABELS: Record<string, string> = {
  static: '静态数据',
  api: 'REST API',
  websocket: 'WebSocket',
  sql: 'SQL 查询',
  mqtt: 'MQTT 订阅',
};

export default function DataBindingPanel() {
  const { components, selectedComponentId, updateComponent } = useDashboardStore();
  const selectedComp = components.find((c) => c.id === selectedComponentId);

  const [datasets, setDatasets] = useState<any[]>([]);
  const [selectedDataSet, setSelectedDataSet] = useState<any | null>(null);
  const [dsData, setDsData] = useState<unknown>(null);
  const [mode, setMode] = useState<'inline' | 'dataset'>(!!selectedComp?.dataSource?.dataSetId ? 'dataset' : 'inline');

  const isRefMode = mode === 'dataset';

  // 跟随选中组件的 dataSetId 同步模式：仅在"有 dataSetId"时强制进 dataset 模式
  // （组件切换/刷新后加载完成）；用户手动切 inline 后，若 dataSetId 仍存在不强制切回。
  const prevCompId = useRef<string | undefined>(undefined);
  useEffect(() => {
    const compId = selectedComp?.id;
    if (compId !== prevCompId.current) {
      prevCompId.current = compId;
      // 切换了组件：按该组件 dataSetId 初始化模式
      setMode(!!selectedComp?.dataSource?.dataSetId ? 'dataset' : 'inline');
    } else if (!!selectedComp?.dataSource?.dataSetId && mode !== 'dataset') {
      // 同一组件：外部加载完成后发现有 dataSetId（如刷新后），进 dataset 模式
      setMode('dataset');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComp?.id, selectedComp?.dataSource?.dataSetId]);

  useEffect(() => {
    fetchDataSources().then((data) => setDatasets(data)).catch(() => {});
  }, []);

  // 引用模式：加载数据集定义 + 拉取数据用于解析预览
  useEffect(() => {
    if (!isRefMode || !selectedComp) return;
    const id = selectedComp.dataSource?.dataSetId;
    const ds = datasets.find((d) => d.id === id);
    setSelectedDataSet(ds || null);
    if (ds) {
      fetchDataSource({ type: ds.type, config: ds.config })
        .then((raw) => setDsData(raw))
        .catch(() => setDsData(null));
    }
  }, [isRefMode, selectedComp?.dataSource?.dataSetId, datasets]);

  if (!selectedComp) {
    return (
      <div style={{ padding: 24, color: '#888' }}>
        <Empty description="选中组件后可配置数据" />
      </div>
    );
  }

  const handleDataSourceChange = (ds: DataSourceConfig) => {
    updateComponent(selectedComp.id, { dataSource: ds });
  };

  const setRefMode = (mode: 'inline' | 'dataset') => {
    if (mode === 'inline') {
      const cur = selectedComp.dataSource;
      const { dataSetId: _omit, ...rest } = (cur || {}) as any;
      void _omit;
      handleDataSourceChange((rest.type ? rest : { type: 'static', config: { data: '[]' } }) as DataSourceConfig);
      setMode('inline');
    } else {
      // 切到引用模式：清空 dataSetId，等待用户明确选择数据集
      handleDataSourceChange({ type: 'static', config: {} } as any);
      setMode('dataset');
    }
  };

  const selectDataSet = (id: string) => {
    const ds = datasets.find((d) => d.id === id);
    setSelectedDataSet(ds || null);
    handleDataSourceChange({ dataSetId: id, type: 'static', config: {} } as any);
    if (ds) {
      fetchDataSource({ type: ds.type, config: ds.config })
        .then((raw) => setDsData(raw))
        .catch(() => setDsData(null));
    }
  };

  const setRefParser = (p: any) => {
    handleDataSourceChange({ ...(selectedComp.dataSource as any), parser: p });
  };

  return (
    <div style={{ padding: 12 }}>
      <Title level={5} style={{ marginBottom: 12, fontSize: 13, color: '#e0e0e0' }}>数据绑定</Title>
      <Text strong style={{ fontSize: 12, color: '#e0e0e0' }}>{selectedComp.type}</Text>

      <Divider style={{ margin: '12px 0', borderColor: '#333' }} />

      {/* 内联 / 引用数据集 切换 */}
      <Space size={8} style={{ marginBottom: 12 }}>
        <span
          onClick={() => setRefMode('inline')}
          style={{
            fontSize: 12, padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
            border: `1px solid ${isRefMode ? '#333' : '#1677ff'}`,
            color: isRefMode ? '#888' : '#fff', background: isRefMode ? 'transparent' : '#1677ff',
          }}
        >内联配置</span>
        <span
          onClick={() => setRefMode('dataset')}
          style={{
            fontSize: 12, padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
            border: `1px solid ${isRefMode ? '#1677ff' : '#333'}`,
            color: isRefMode ? '#fff' : '#888', background: isRefMode ? '#1677ff' : 'transparent',
          }}
        >引用数据集</span>
      </Space>

      {isRefMode ? (
        <div>
          <Text style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 8 }}>选择数据集（连接+查询+解析复用）</Text>
          <Select
            style={{ width: '100%' }}
            placeholder="选择数据集"
            value={selectedComp.dataSource?.dataSetId}
            onChange={selectDataSet}
            onDropdownVisibleChange={(open) => {
              // 打开下拉时刷新数据集列表（新建的数据集立即可见）
              if (open) {
                fetchDataSources().then((data) => setDatasets(data)).catch(() => {});
              }
            }}
            options={datasets.map((d) => ({
              value: d.id,
              label: `${d.name}（${TYPE_LABELS[d.type] || d.type}）`,
            }))}
          />
          {selectedDataSet && (
            <div style={{ marginTop: 10, background: '#151528', border: '1px solid #2a2a3e', borderRadius: 4, padding: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Tag color="blue">{TYPE_LABELS[selectedDataSet.type] || selectedDataSet.type}</Tag>
                <Text style={{ fontSize: 11, color: '#888' }}>
                  {selectedDataSet.refreshInterval ? `刷新 ${selectedDataSet.refreshInterval}s` : '不轮询'}
                </Text>
              </div>
              <Text style={{ fontSize: 11, color: '#555', display: 'block' }}>
                数据集已含解析管线，组件可叠加二次解析
              </Text>
              <Divider style={{ margin: '10px 0', borderColor: '#2a2a3e' }} />
              <DataParserEditor
                parser={(selectedComp.dataSource as any)?.parser}
                rawData={dsData}
                onChange={setRefParser}
              />
            </div>
          )}
          {datasets.length === 0 && (
            <Text style={{ fontSize: 12, color: '#faad14', display: 'block', marginTop: 8 }}>
              暂无数据集，请到「数据集管理」页创建
            </Text>
          )}
        </div>
      ) : (
        <DataSourceConfigForm
          value={selectedComp.dataSource as DataSourceConfig}
          onChange={handleDataSourceChange}
          widgetType={selectedComp.type}
        />
      )}
    </div>
  );
}
